import { useMutation, useQuery } from '@apollo/client';
import {
  M,
  isAfterBreakpoint,
  usePlayerFromContext,
  useVideoContext,
  useViewportContext,
} from '@loomhq/createhq/dist/video-player';

import { USER } from 'constants/ssr';
import { replaceURLs } from 'utilities/mediumHelpers';

import CreateComment from './CreateComment.gql';
import DeleteComment from './DeleteComment.gql';
import FetchComments from './FetchComments.gql';
import RestoreComment from './RestoreComment.gql';
import { useErrorStore } from '../error-layer/ErrorStoreProvider';
import { usePasswordContext } from '../password/context';
import {
  CommentFromServer,
  CommentOrReply,
  isReply,
  ReplyFromServer,
} from '../utils';
import { getAnonUserId } from 'utilities/auth';

const COMMENT_CREATE_ERROR = 'Error creating comment';
const COMMENT_FETCH_ERROR = 'Error fetching comments';
const COMMENT_DELETE_ERROR = 'Error deleting comment';
const COMMENT_RESTORE_ERROR = 'Error deleting or restoring comment';

export function useCommentsFullsize(): boolean {
  const { widthBreakpoint } = useViewportContext();
  const afterM = isAfterBreakpoint(M, widthBreakpoint);

  return afterM;
}

export function useFetchComments(): {
  comments: CommentFromServer[];
  refetch: any;
} {
  const { video } = useVideoContext();
  const { password } = usePasswordContext();
  const { pushError } = useErrorStore();

  const { data, refetch } = useQuery(FetchComments, {
    variables: { id: video.modelId, password },

    onError: () => {
      pushError(COMMENT_FETCH_ERROR);
    },
  });

  return { comments: data?.video?.video_comments, refetch };
}

export function useDeleteComment(comment: CommentOrReply): any {
  const { password } = usePasswordContext();
  const { pushError } = useErrorStore();

  // we do not re-fetch all comments on delete/restore, because we handle the restore placeholder locally
  const [deleteComment] = useMutation(DeleteComment, {
    variables: {
      type: isReply(comment) ? 'REPLY' : 'COMMENT',
      id: comment.id,
      password,
    },
    onError: () => {
      pushError(COMMENT_DELETE_ERROR);
    },
    update: cache => {
      cache.modify({
        id: cache.identify(comment),
        fields: {
          locallyDeleted: () => true,
        },
      });
    },
  });

  return deleteComment;
}

export function useRestoreComment(comment: CommentOrReply): any {
  const { password } = usePasswordContext();
  const { pushError } = useErrorStore();

  // we do not re-fetch all comments on delete/restore, because we handle the restore placeholder locally
  const [restoreComment] = useMutation(RestoreComment, {
    variables: {
      type: isReply(comment) ? 'REPLY' : 'COMMENT',
      id: comment.id,
      password,
    },
    onError: () => {
      pushError(COMMENT_RESTORE_ERROR);
    },
    update: cache => {
      cache.modify({
        id: cache.identify(comment),
        fields: {
          locallyDeleted: () => false,
        },
      });
    },
  });

  return restoreComment;
}

export function useCreateComment(): any {
  const { video } = useVideoContext();
  const { password } = usePasswordContext();
  const player = usePlayerFromContext();
  const { pushError } = useErrorStore();

  const createComment = ({
    content,
    parentPostId,
  }: {
    content: string;
    parentPostId?: string;
  }) => {
    createCommentInternal({
      variables: {
        videoId: video.modelId,
        timestamp: Math.round(player?.currentTime || 0),
        content: replaceURLs(content),
        parentPostId,
        password,
        anonUserName: '',
      },
    });
  };

  const [createCommentInternal] = useMutation(CreateComment, {
    onError: () => {
      pushError(COMMENT_CREATE_ERROR);
    },
    update: (cache, data) => {
      const newlyCreatedComment = data.data.createVideoComment;
      const reply = isReply(newlyCreatedComment);

      cache.updateQuery(
        {
          query: FetchComments,
          variables: { id: video.modelId, password },
        },
        cacheData => {
          const currentComments = cacheData.video.video_comments;

          let newComments;

          if (!reply) {
            // Case 1: this is a comment, we can add it to `video_comments`
            newComments = [...currentComments, newlyCreatedComment];
          } else {
            // case 2: this is a reply, we need to insert it into one of our comments

            // copy the current comments so we can replace one element
            newComments = currentComments.slice();

            const parentId = newComments.findIndex(
              c => c.id === newlyCreatedComment.comment_post_id.toString()
            );

            const parent = newComments[parentId];

            // replace with new parent, which holds new reply
            newComments[parentId] = {
              ...parent,
              children_comments: [
                ...parent.children_comments,
                newlyCreatedComment,
              ],
            };
          }

          // in both cases: update the comments in the cache
          return {
            video: {
              ...cacheData.video,
              video_comments: newComments,
            },
          };
        }
      );
    },
  });

  return createComment;
}

export function useCanDelete(
  commentOrReply: CommentFromServer | ReplyFromServer
): boolean {
  const { video } = useVideoContext();

  const isVideoOwner = video.isOwner;
  const isCommentAuthor = commentOrReply.user_id === (window as any)[USER]?.id;
  const isAnonCommentAuthor = commentOrReply?.anon_user_id === getAnonUserId();

  return isVideoOwner || isCommentAuthor || isAnonCommentAuthor;
}
